/**
 * Registrul de blocuri si randarea unei pagini.
 *
 * Registrul e o harta COMPLETA peste `MarketBlockType`, verificata la compilare:
 * `Record<MarketBlockType, ...>` face ca un tip adaugat in catalog fara renderer
 * sa opreasca build-ul. Fara asta, blocul ar fi trecut de validarea core-ului,
 * ar fi aparut in editor, s-ar fi publicat cu succes si pur si simplu n-ar fi
 * fost pe pagina — cel mai greu fel de defect de observat, fiindca nimic nu
 * raporteaza nimic.
 */

import type { ComponentType } from "react";

import {
  HOME_PAGE_SLUG,
  blockText,
  blocksForPage,
  blocksFromLegacySections,
  type MarketBlockType,
  type MarketPageBlock,
  type PublicMarketConfig,
} from "@deliveryapp/shared";

import type { BlockContext, BlockProps } from "./context";
import {
  AddressBarBlock,
  ContactBlock,
  CoverageListBlock,
  HoursBlock,
  MenuHighlightsBlock,
  PaymentNoteBlock,
  StorefrontGridBlock,
} from "./data";
import {
  CtaBannerBlock,
  DividerBlock,
  FaqBlock,
  FeaturesBlock,
  GalleryBlock,
  HeroBlock,
  HeroSearchBlock,
  ImageTextBlock,
  LegalNoteBlock,
  PromoStripBlock,
  StatsBlock,
  StepsBlock,
  TestimonialsBlock,
  TextBlock,
} from "./editorial";
import { ReservationsBlock } from "./reservations";
import { carriesHeading } from "./heading";
import { useBootstrap } from "../components/BootstrapProvider";

export type { BlockContext } from "./context";

const RENDERERS: Record<MarketBlockType, ComponentType<BlockProps>> = {
  hero: HeroBlock,
  "hero-search": HeroSearchBlock,
  "address-bar": AddressBarBlock,
  "storefront-grid": StorefrontGridBlock,
  "menu-highlights": MenuHighlightsBlock,
  "coverage-list": CoverageListBlock,
  steps: StepsBlock,
  features: FeaturesBlock,
  text: TextBlock,
  "image-text": ImageTextBlock,
  gallery: GalleryBlock,
  stats: StatsBlock,
  testimonials: TestimonialsBlock,
  faq: FaqBlock,
  hours: HoursBlock,
  contact: ContactBlock,
  reservations: ReservationsBlock,
  "cta-banner": CtaBannerBlock,
  "promo-strip": PromoStripBlock,
  "payment-note": PaymentNoteBlock,
  "legal-note": LegalNoteBlock,
  divider: DividerBlock,
};


/**
 * Blocurile paginii cerute, din configul publicat.
 *
 * Cand piata nu are inca `blocks` (publicata inainte de builder), pagina de start
 * se compune din vechiul `homeSections`. Traducerea NU se scrie in baza: o
 * rescriere tacita a configului publicat al unei piete vii ar fi schimbat ce vad
 * clientii fara ca cineva sa fi cerut o publicare.
 */
export function resolvePageBlocks(config: PublicMarketConfig, pageSlug: string): MarketPageBlock[] {
  const declared = config.layout.blocks;
  if (Array.isArray(declared) && declared.length > 0) return blocksForPage(declared, pageSlug);
  if (pageSlug !== HOME_PAGE_SLUG) return [];
  return blocksFromLegacySections(config);
}

export function BlockStack({
  blocks,
  ctx,
  /**
   * `false` cand pagina isi scrie singura titlul de nivel 1 (paginile in plus il
   * au din datele paginii). Atunci niciun bloc nu mai cere nivelul 1.
   */
  ownsHeading = true,
  /**
   * Titlul de rezerva al paginii, cand niciun bloc nu afiseaza unul.
   *
   * Se randeaza ascuns vizual, dar prezent in document: pagina de start compusa
   * din blocuri fara titlu (bara de adresa + restaurante + o banda de anunt) e o
   * alegere legitima de design, dar un document fara `h1` nu e.
   */
  fallbackHeading,
}: {
  blocks: MarketPageBlock[];
  ctx: BlockContext;
  ownsHeading?: boolean;
  fallbackHeading?: string;
}) {
  const { bootstrap } = useBootstrap();
  const storefrontCount = bootstrap.storefronts.length;
  const firstHeadingIndex = ownsHeading
    ? blocks.findIndex((block) => carriesHeading(block, storefrontCount))
    : -1;
  const needsFallback =
    ownsHeading && firstHeadingIndex === -1 && fallbackHeading !== undefined && fallbackHeading.length > 0;

  return (
    <div className="flex flex-col gap-8">
      {needsFallback ? <h1 className="sr-only">{fallbackHeading}</h1> : null}
      {blocks.map((block, index) => {
        const Renderer = RENDERERS[block.type];
        // Piata poate rula o imagine web mai veche decat configul publicat
        // (ADR-002): un tip necunoscut se SARE, nu opreste pagina.
        if (Renderer === undefined) return null;
        return (
          <Renderer
            key={block.id}
            block={block}
            ctx={ctx}
            headingLevel={index === firstHeadingIndex ? 1 : 2}
          />
        );
      })}
    </div>
  );
}
